<script setup lang="ts">
import { computed, ref } from 'vue'
import { Tags } from '@lucide/vue'
import { Badge, Button, PickerDialog } from 'catalyst-vue'
import type { Category } from '@tanko/shared'
import { useControlSize } from '../../lib/library-layout'

/**
 * 分類指派：一顆觸發按鈕 + 已選分類的 Badge，點擊後開啟可搜尋的多選 PickerDialog。
 *
 * 沒有用 Dropdown 做成停留式多選選單，因為 DropdownItem 觸發時無條件呼叫 closeTree，
 * 每勾一個就會關閉選單。這個缺口已記錄在套件的 open-questions.md。
 */
const props = withDefaults(defineProps<{ categories: Category[]; disabled?: boolean; legend?: string }>(), {
  disabled: false, legend: '放進哪些分類',
})
const selected = defineModel<string[]>({ required: true })
const controlSize = useControlSize()
const open = ref(false)
const chosen = computed(() => props.categories.filter(category => selected.value.includes(category.id)))

function apply(keys: (string | number)[]) {
  const valid = new Set(props.categories.map(category => category.id))
  selected.value = keys.map(String).filter(id => valid.has(id))
}
</script>

<template>
  <div class="category-picker-field">
    <div class="category-picker-head">
      <span class="category-picker-legend">{{ legend }}</span>
      <Button outline :size="controlSize" :disabled="disabled" aria-haspopup="dialog" @click="open = true">
        <Tags :size="15" aria-hidden="true" />{{ chosen.length ? `已選 ${chosen.length} 個` : '選擇分類' }}
      </Button>
    </div>
    <div v-if="chosen.length" class="category-picker-badges">
      <Badge v-for="category in chosen" :key="category.id" color="primary">{{ category.name }}</Badge>
    </div>
    <p v-else class="muted-copy">尚未指派分類。</p>
    <PickerDialog
      :open="open"
      :items="categories"
      :model-value="selected"
      :item-key="(category: Category) => category.id"
      :item-text="(category: Category) => category.name"
      selection-mode="multiple"
      :title="legend"
      description="可複選。分類的新增與刪除請到「分類管理」。"
      search-label="搜尋分類"
      search-placeholder="輸入分類名稱"
      empty-message="還沒有任何分類，先到「分類管理」新增一個。"
      confirm-label="套用"
      @apply="apply"
      @update:open="open = $event"
    />
  </div>
</template>
